import { keepPreviousData, queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BaseResultado, BuscaEventos, DetalheEvento, ResultadoDia, Afiliados, DecisaoSaqueCarteira, FiltroSaquesCarteira, ListaSaquesCarteira, SaqueCarteira, LinkPortal, PedidoLinkPortal, AcessoCadastrado, AcessoComToken, AlteracaoAcesso, AlteracaoMultipla, BuscaSecao, Capa, EntradaMontagem, IconeLiga, ImagemDestaque, ItemOrdem, LigaCriada, Midia, NovaImagem, NovaLiga, ListaAcessos, ListaMultiplas, Montagem, Multipla, NovaMultipla, NovoAcesso, Ordenacao, SecaoComItens, SecaoOrdenavel, AoVivo, ApostasPorDia, BilhetesDoJogador, CatalogoEventos, DetalheJogo, DetalhePadrao, DetalheJogoCassino, Esquema, Eu, JogosCassino, Niveis, EstatisticasPadrao, LiabilityEsportes, ListaSaques, ModoLiability, SimilaresBilhete, PagamentosPorDia, Periodo, ResultadoBusca, Resumo, ResumoJogador, RiscoEsportes, Situacao, MercadosEvento, FiltroLongoPrazo, ListaLongoPrazo } from '@bi-evox/contrato';
import { enviar, enviarBinario, obter } from './cliente';

// Uma chave por rota + período. O staleTime acompanha o cache de 5 min do backend;
// keepPreviousData evita a tela piscar ao trocar o período.
const porPeriodo = <T,>(rota: string, p: Periodo) =>
  queryOptions({
    queryKey: [rota, p.de, p.ate],
    queryFn: () => obter<T>(`/v1/${rota}`, { de: p.de, ate: p.ate }),
    placeholderData: keepPreviousData,
  });

export const consultas = {
  // Quem entrou: perfis e telas mudam só com novo login.
  eu: () => queryOptions({ queryKey: ['eu'], queryFn: () => obter<Eu>('/v1/eu'), staleTime: Infinity }),
  situacao: () =>
    queryOptions({
      queryKey: ['situacao'],
      queryFn: () => obter<Situacao>('/v1/situacao'),
      staleTime: 60_000,
      refetchInterval: 120_000,
    }),
  esquema: () => queryOptions({ queryKey: ['esquema'], queryFn: () => obter<Esquema>('/v1/esquema'), staleTime: 30 * 60_000 }),
  resumo: (p: Periodo) => porPeriodo<Resumo>('resumo', p),
  pagamentosPorDia: (p: Periodo) => porPeriodo<PagamentosPorDia>('pagamentos/por-dia', p),
  afiliados: (p: Periodo) => porPeriodo<Afiliados>('afiliados', p),
  apostasPorDia: (p: Periodo) => porPeriodo<ApostasPorDia>('apostas/por-dia', p),
  jogosCassino: (p: Periodo) => porPeriodo<JogosCassino>('cassino/jogos', p),
  niveis: (p: Periodo) => porPeriodo<Niveis>('niveis', p),
  // Risco acompanha o cache de 1 min do backend; só atualiza sozinho quando o dia é hoje.
  riscoEsportes: (dia: string, hoje: boolean) =>
    queryOptions({
      queryKey: ['risco/esportes', dia],
      queryFn: () => obter<RiscoEsportes>('/v1/risco/esportes', { dia }),
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      refetchInterval: hoje ? 60_000 : false,
    }),
  aoVivo: (ligado: boolean) =>
    queryOptions({
      queryKey: ['ao-vivo'],
      queryFn: () => obter<AoVivo>('/v1/ao-vivo'),
      staleTime: 15_000,
      refetchInterval: ligado ? 20_000 : false,
      enabled: ligado,
    }),
};

export const useEu = () => useQuery(consultas.eu());
export const useSituacao = () => useQuery(consultas.situacao());
export const useEsquema = () => useQuery(consultas.esquema());
export const useResumo = (p: Periodo) => useQuery(consultas.resumo(p));
export const usePagamentosPorDia = (p: Periodo) => useQuery(consultas.pagamentosPorDia(p));
export const useAfiliados = (p: Periodo) => useQuery(consultas.afiliados(p));
/** "Ver como" e convite da carteira do afiliado: cada link é único, nada vai ao cache. */
export const useLinkPortal = () =>
  useMutation({ mutationFn: ({ conta, ...pedido }: PedidoLinkPortal & { conta: number }) => enviar<LinkPortal>(`/v1/afiliados/${conta}/links`, pedido) });
/** Fila de saques pagos pela carteira: sem cache no backend; aqui, 30 s e de novo a cada decisão. */
export const useSaquesCarteira = (filtro: FiltroSaquesCarteira) =>
  useQuery({ queryKey: ['afiliados/saques', filtro], queryFn: () => obter<ListaSaquesCarteira>('/v1/afiliados/saques', { situacao: filtro }), staleTime: 30_000, refetchInterval: 60_000 });
export const useDecidirSaque = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...d }: DecisaoSaqueCarteira & { id: number }) => enviar<SaqueCarteira>(`/v1/afiliados/saques/${id}`, d),
    onSettled: () => qc.invalidateQueries({ queryKey: ['afiliados/saques'] }),
  });
};
export const useApostasPorDia = (p: Periodo) => useQuery(consultas.apostasPorDia(p));
export const useJogosCassino = (p: Periodo) => useQuery(consultas.jogosCassino(p));
export const useNiveis = (p: Periodo) => useQuery(consultas.niveis(p));
/** Jogadores de um jogo do cassino; sem dado pessoal, acompanha o cache de 5 min do backend. */
export const useDetalheJogoCassino = (p: Periodo, provedor: string, jogo: string) =>
  useQuery({
    queryKey: ['cassino/jogo', p.de, p.ate, provedor, jogo],
    queryFn: () => obter<DetalheJogoCassino>('/v1/cassino/jogo', { de: p.de, ate: p.ate, provedor, jogo }),
  });
export const useRiscoEsportes = (dia: string, hoje: boolean) => useQuery(consultas.riscoEsportes(dia, hoje));
export const useAoVivo = (ligado: boolean) => useQuery(consultas.aoVivo(ligado));
/** Horário e escudos dos jogos: o backend guarda por 10 minutos. */
export const useCatalogo = () =>
  useQuery({
    queryKey: ['altenar/eventos'],
    queryFn: () => obter<CatalogoEventos>('/v1/altenar/eventos'),
    staleTime: 5 * 60_000,
    refetchInterval: 10 * 60_000,
  });
export const useDetalheJogo = (dia: string, jogo: string, hoje: boolean) =>
  useQuery({
    queryKey: ['risco/jogo', dia, jogo],
    queryFn: () => obter<DetalheJogo>('/v1/risco/jogo', { dia, jogo }),
    staleTime: 15_000,
    refetchInterval: hoje ? 30_000 : false,
  });
/** Resultado dos fechados do dia por jogo; só busca com a aba aberta e, hoje, atualiza a cada minuto. */
export const useResultadoDia = (dia: string, base: BaseResultado, hoje: boolean, ligado: boolean) =>
  useQuery({
    queryKey: ['risco/resultado', dia, base],
    queryFn: () => obter<ResultadoDia>('/v1/risco/resultado', { dia, base }),
    enabled: ligado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: ligado && hoje ? 60_000 : false,
  });
/** Betlist: eventos com o termo no nome, entre as apostas dos últimos dias. */
export const useBuscaEventos = (termo: string, dias: number) =>
  useQuery({
    queryKey: ['risco/eventos', termo, dias],
    queryFn: () => obter<BuscaEventos>('/v1/risco/eventos', { termo, dias: String(dias) }),
    enabled: termo.trim().length >= 3,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
export const useDetalheEvento = (jogo: string, dias: number) =>
  useQuery({
    queryKey: ['risco/evento', jogo, dias],
    queryFn: () => obter<DetalheEvento>('/v1/risco/evento', { jogo, dias: String(dias) }),
    enabled: Boolean(jogo),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
/** Bilhetes em aberto agora; só busca com a aba aberta e atualiza a cada minuto (cache do backend). */
export const useLiability = (modo: ModoLiability, soMaisAtivo: boolean, ligado: boolean) =>
  useQuery({
    queryKey: ['risco/liability', modo, soMaisAtivo],
    queryFn: () => obter<LiabilityEsportes>('/v1/risco/liability', { modo, mercados: soMaisAtivo ? 'mais-ativo' : 'todos' }),
    enabled: ligado,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    refetchInterval: ligado ? 60_000 : false,
  });
export const useBilhete = (id: string) =>
  useQuery({
    queryKey: ['risco/bilhete', id],
    queryFn: () => obter<SimilaresBilhete>('/v1/risco/bilhete', { id }),
    staleTime: 30_000,
  });
export const useBilhetesDoJogador = (id: number, p: Periodo) =>
  useQuery({
    queryKey: ['jogadores/bilhetes', id, p.de, p.ate],
    queryFn: () => obter<BilhetesDoJogador>(`/v1/jogadores/${id}/bilhetes`, { de: p.de, ate: p.ate }),
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

/** Só busca depois do botão da gaveta; aí acompanha o placar a cada 20 s. */
export const useEstatisticasPadrao = (dia: string, id: string, ligado: boolean) =>
  useQuery({
    queryKey: ['risco/esportes/padrao/estatisticas', dia, id],
    queryFn: () => obter<EstatisticasPadrao>('/v1/risco/esportes/padrao/estatisticas', { dia, id }),
    enabled: ligado,
    staleTime: 15_000,
    refetchInterval: ligado ? 20_000 : false,
  });
export const useDetalhePadrao = (dia: string, id: string, hoje: boolean) =>
  useQuery({
    queryKey: ['risco/esportes/padrao', dia, id],
    queryFn: () => obter<DetalhePadrao>('/v1/risco/esportes/padrao', { dia, id }),
    staleTime: 30_000,
    refetchInterval: hoje ? 60_000 : false,
  });

// Jogadores: o resumo sai do cache do navegador logo (tem e-mail) e não fica guardado entre telas.
export const useResumoJogador = (id: number | null, p: Periodo) =>
  useQuery({
    queryKey: ['jogador', id, p.de, p.ate],
    queryFn: () => obter<ResumoJogador>(`/v1/jogadores/${id}`, { de: p.de, ate: p.ate }),
    enabled: id !== null,
    gcTime: 60_000,
    placeholderData: keepPreviousData,
  });
/** Busca por id ou e-mail; o termo vai no corpo do POST, nunca na URL nem na chave do cache. */
export const useBuscaJogador = () => useMutation({ mutationFn: (termo: string) => enviar<ResultadoBusca>('/v1/jogadores/busca', { termo }) });

// Saques: sem dado pessoal (jogador só por id). A lista acompanha o cache de 1 min do backend.
export const useUltimosSaques = () =>
  useQuery({ queryKey: ['saques'], queryFn: () => obter<ListaSaques>('/v1/saques'), staleTime: 30_000, refetchInterval: 60_000 });
/** Busca pelo id do gateway; só roda depois do botão (termo vazio não busca). */
export const useBuscaSaque = (termo: string) =>
  useQuery({
    queryKey: ['saques/busca', termo],
    queryFn: () => obter<ListaSaques>('/v1/saques/busca', { termo }),
    enabled: termo !== '',
    staleTime: 30_000,
    retry: false,
  });

// Acessos (só admin): sem staleTime, cada mudança recarrega a lista.
export const useAcessos = () => useQuery({ queryKey: ['acessos'], queryFn: () => obter<ListaAcessos>('/v1/acessos'), staleTime: 0 });

const useMudarAcessos = <A, R>(fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['acessos'] }) });
};
export const useCriarAcesso = () => useMudarAcessos((n: NovoAcesso) => enviar<AcessoComToken>('/v1/acessos', n));
export const useAlterarAcesso = () =>
  useMudarAcessos(({ id, ...a }: AlteracaoAcesso & { id: string }) => enviar<AcessoCadastrado>(`/v1/acessos/${encodeURIComponent(id)}`, a));
export const useTrocarToken = () => useMudarAcessos((id: string) => enviar<AcessoComToken>(`/v1/acessos/${encodeURIComponent(id)}/token`, {}));
export const useRemoverAcesso = () => useMudarAcessos((id: string) => enviar<{ removido: string }>(`/v1/acessos/${encodeURIComponent(id)}/remover`, {}));

// Conteúdo do site (tela conteudo): o backend repassa ao Worker do site; sem staleTime, muda na hora.
export const useMultiplas = () => useQuery({ queryKey: ['conteudo', 'multiplas'], queryFn: () => obter<ListaMultiplas>('/v1/conteudo/multiplas'), staleTime: 0 });
/** Só monta o payload no Worker (busca os eventos na Altenar); não grava. */
export const useMontarMultipla = () => useMutation({ mutationFn: (e: EntradaMontagem) => enviar<Montagem>('/v1/conteudo/multiplas/montar', e) });
const useMudarMultiplas = <A, R>(fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['conteudo', 'multiplas'] }) });
};
export const useCriarMultipla = () => useMudarMultiplas((n: NovaMultipla) => enviar<Multipla>('/v1/conteudo/multiplas', n));
export const useAlterarMultipla = () =>
  useMudarMultiplas(({ id, ...a }: AlteracaoMultipla & { id: string }) => enviar<Multipla>(`/v1/conteudo/multiplas/${encodeURIComponent(id)}`, a));
export const useRemoverMultipla = () => useMudarMultiplas((id: string) => enviar<{ removida: string }>(`/v1/conteudo/multiplas/${encodeURIComponent(id)}/remover`, {}));

// Ordem das seções da home: a lista ao vivo muda a cada minuto, então sem staleTime.
export const useSecao = (secao: SecaoOrdenavel) => useQuery({ queryKey: ['conteudo', 'secoes', secao], queryFn: () => obter<SecaoComItens>(`/v1/conteudo/secoes/${secao}`), staleTime: 0 });
export const useGravarOrdem = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ secao, itens }: { secao: SecaoOrdenavel; itens: ItemOrdem[] }) => enviar<Ordenacao>(`/v1/conteudo/secoes/${secao}`, { itens }),
    onSettled: (_r, _e, { secao }) => qc.invalidateQueries({ queryKey: ['conteudo', 'secoes', secao] }),
  });
};
/** Busca para fixar na ordem: só dispara quando pedido. */
export const useBuscarSecao = () => useMutation({ mutationFn: ({ secao, termo }: { secao: SecaoOrdenavel; termo: string }) => obter<BuscaSecao>(`/v1/conteudo/secoes/${secao}/buscar`, { q: termo }) });
export const useEnviarMidia = () =>
  useMutation({ mutationFn: ({ arquivo, largura, altura }: { arquivo: Blob; largura: number; altura: number }) => enviarBinario<Midia>('/v1/conteudo/midias', arquivo, { 'x-largura': String(largura), 'x-altura': String(altura) }) });
export const useGravarCapa = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventoId, midiaId }: { eventoId: string; midiaId: string | null }) => enviar<{ eventoId: string; capa: Capa | null }>(`/v1/conteudo/capas/${encodeURIComponent(eventoId)}`, { midiaId }),
    onSettled: () => qc.invalidateQueries({ queryKey: ['conteudo', 'secoes', 'destaques'] }),
  });
};
// Cards só de imagem nos destaques: aparecem na lista de ordem dos destaques, então ela recarrega.
const useMudarImagens = <A, R>(fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['conteudo', 'secoes', 'destaques'] }) });
};
export const useCriarImagem = () => useMudarImagens((n: NovaImagem) => enviar<ImagemDestaque>('/v1/conteudo/imagens', n));
export const useAlterarImagem = () => useMudarImagens(({ id, ...a }: Partial<NovaImagem> & { id: string }) => enviar<ImagemDestaque>(`/v1/conteudo/imagens/${encodeURIComponent(id)}`, a));
export const useRemoverImagem = () => useMudarImagens((id: string) => enviar<{ removida: string }>(`/v1/conteudo/imagens/${encodeURIComponent(id)}/remover`, {}));
// Ligas criadas e ícones: aparecem na lista de ordem das ligas populares, então ela recarrega.
const useMudarLigas = <A, R>(fn: (a: A) => Promise<R>) => {
  const qc = useQueryClient();
  return useMutation({ mutationFn: fn, onSettled: () => qc.invalidateQueries({ queryKey: ['conteudo', 'secoes', 'ligas'] }) });
};
export const useCriarLiga = () => useMudarLigas((n: NovaLiga) => enviar<{ liga: LigaCriada; icone: IconeLiga | null }>('/v1/conteudo/ligas', n));
export const useRemoverLiga = () => useMudarLigas((id: string) => enviar<{ removida: string }>(`/v1/conteudo/ligas/${encodeURIComponent(id)}/remover`, {}));
export const useGravarIconeLiga = () =>
  useMudarLigas(({ ligaId, midiaId }: { ligaId: string; midiaId: string | null }) => enviar<{ ligaId: string; icone: IconeLiga | null }>(`/v1/conteudo/ligas/${encodeURIComponent(ligaId)}/icone`, { midiaId }));
/** Mercados de um jogo para montar a múltipla clicando nas odds. */
export const useMercadosEvento = () => useMutation({ mutationFn: (eventId: number) => obter<MercadosEvento>(`/v1/conteudo/eventos/${eventId}/mercados`) });
/** Mercados de longo prazo (outrights) pelo link do 99play ou pelos ids. */
export const useLongoPrazo = () =>
  useMutation({
    mutationFn: (f: FiltroLongoPrazo) => {
      const params: Record<string, string> = {};
      if (f.link) params.link = f.link;
      if (f.sportId) params.sportId = String(f.sportId);
      if (f.categoryId) params.categoryId = String(f.categoryId);
      if (f.championshipId) params.championshipId = String(f.championshipId);
      return obter<ListaLongoPrazo>('/v1/conteudo/longo-prazo', params);
    },
  });
